import { create } from "zustand";
import {
  fetchTaxYearData,
  saveTaxYearData,
  fetchUserPreferences,
  saveUserPreferences,
} from "../../../services/taxApi";
import {
  fetchRecurringIncomeTaxProjection,
  fetchRecurringIncomeMonthlyBreakdown,
  type TaxProjection,
  type MonthlyBreakdown,
} from "../../../services/recurringIncomeApi";
import {
  defaultTaxYearInput,
  TAX_YEAR_CE_MIN,
  TAX_YEAR_CE_MAX,
  ALL_DEDUCTION_KEYS,
  DeductionKey,
} from "../types/tax.types";
import type { TaxYearInput } from "../types/tax.types";

interface TaxStoreState {
  viewYear: number; // CE year (2023–2040)
  yearData: Record<number, TaxYearInput>;
  recurringProjection: TaxProjection | null;
  monthlyBreakdown: MonthlyBreakdown | null;
  enabledDeductions: DeductionKey[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  setYear: (year: number) => void;
  updateInput: <K extends keyof TaxYearInput>(
    field: K,
    value: TaxYearInput[K],
  ) => void;
  toggleDeduction: (key: DeductionKey) => void;
  loadYearData: (year: number) => Promise<void>;
  saveYearData: (year: number) => Promise<void>;
  loadPreferences: () => Promise<void>;
  savePreferences: () => Promise<void>;
}

function clampYear(year: number) {
  return Math.max(TAX_YEAR_CE_MIN, Math.min(TAX_YEAR_CE_MAX, year));
}

const currentCEYear = clampYear(new Date().getFullYear());

export const useTaxStore = create<TaxStoreState>()((set, get) => ({
  viewYear: currentCEYear,
  yearData: {},
  recurringProjection: null,
  monthlyBreakdown: null,
  enabledDeductions: [...ALL_DEDUCTION_KEYS],
  loading: false,
  saving: false,
  error: null,

  toggleDeduction(key) {
    set((state) => ({
      enabledDeductions: state.enabledDeductions.includes(key)
        ? state.enabledDeductions.filter((k) => k !== key)
        : [...state.enabledDeductions, key],
    }));
    get().savePreferences();
  },

  setYear(year) {
    const clamped = clampYear(year);
    set({ viewYear: clamped });
    get().loadYearData(clamped);
  },

  updateInput(field, value) {
    const { viewYear, yearData } = get();
    const current = yearData[viewYear] ?? { ...defaultTaxYearInput };
    const updated = { ...current, [field]: value };
    set({ yearData: { ...yearData, [viewYear]: updated } });
    get().saveYearData(viewYear);
  },

  async loadYearData(year) {
    set({ loading: true, error: null });
    try {
      const [dataResult, projectionResult, breakdownResult] =
        await Promise.allSettled([
          fetchTaxYearData(year),
          fetchRecurringIncomeTaxProjection(year),
          fetchRecurringIncomeMonthlyBreakdown(year),
        ]);

      if (dataResult.status === "rejected") throw dataResult.reason;

      const data = dataResult.value;
      const projection =
        projectionResult.status === "fulfilled"
          ? projectionResult.value
          : { employment40_1: 0, freelance40_8: 0 };
      const breakdown =
        breakdownResult.status === "fulfilled" ? breakdownResult.value : null;

      if (projectionResult.status === "rejected")
        console.warn("[tax] projection fetch failed:", projectionResult.reason);
      if (breakdownResult.status === "rejected")
        console.warn("[tax] breakdown fetch failed:", breakdownResult.reason);

      set((state) => {
        const base = data ?? state.yearData[year] ?? {};
        const merged: TaxYearInput = {
          ...defaultTaxYearInput,
          ...base,
          employmentIncome: projection.employment40_1,
          freelanceRevenue: projection.freelance40_8,
        };
        return {
          yearData: { ...state.yearData, [year]: merged },
          recurringProjection: projection,
          monthlyBreakdown: breakdown,
        };
      });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Failed to load" });
      console.error("[tax] loadYearData:", err);
    } finally {
      set({ loading: false });
    }
  },

  async saveYearData(year) {
    const { yearData } = get();
    const data = yearData[year];
    if (!data) return;
    set({ saving: true });
    try {
      await saveTaxYearData(year, data);
    } catch (err) {
      console.error("[tax] saveYearData:", err);
    } finally {
      set({ saving: false });
    }
  },

  async loadPreferences() {
    try {
      const prefs = await fetchUserPreferences();
      if (prefs) {
        set({ enabledDeductions: prefs.enabledDeductions });
      }
    } catch (err) {
      console.error("[tax] loadPreferences:", err);
    }
  },

  async savePreferences() {
    const { enabledDeductions } = get();
    try {
      await saveUserPreferences({ enabledDeductions });
    } catch (err) {
      console.error("[tax] savePreferences:", err);
    }
  },
}));

export function useCurrentTaxInput(): TaxYearInput {
  const { viewYear, yearData } = useTaxStore();
  return { ...defaultTaxYearInput, ...yearData[viewYear] };
}

export function useRecurringProjection() {
  return useTaxStore((s) => s.recurringProjection);
}

export function useMonthlyBreakdown() {
  return useTaxStore((s) => s.monthlyBreakdown);
}
